import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Play, Zap, Shield, GitBranch } from 'lucide-react';
import { orchestrationMetrics } from '../../data/enhancedAgentsData';

interface WorkflowTile {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'draft' | 'inactive';
  executions: number;
  lastRun: string;
  successRate: number;
  orchestrationType: 'native' | 'hosted' | 'hybrid';
  agentMix: string;
}

const workflowTiles: WorkflowTile[] = [
  {
    id: 'submission-intake',
    name: 'Submission Intake & Analysis',
    description: 'Complete submission processing with native ElevateNow intelligence agents',
    status: 'active',
    executions: 15234,
    lastRun: '2 hours ago',
    successRate: 97.3,
    orchestrationType: 'native',
    agentMix: 'all_native'
  },
  {
    id: 'claims-processing',
    name: 'Claims Processing & Investigation',
    description: 'Hosted agent integration with AWS Agent Code for fraud detection and settlement',
    status: 'active',
    executions: 8945,
    lastRun: '30 mins ago',
    successRate: 96.4,
    orchestrationType: 'hosted',
    agentMix: 'external_integration'
  },
  {
    id: 'regulatory-filing',
    name: 'Regulatory Filing & Compliance',
    description: 'Hybrid orchestration combining native governance with hosted regulatory monitoring',
    status: 'active',
    executions: 5623,
    lastRun: '1 hour ago',
    successRate: 99.1,
    orchestrationType: 'hybrid',
    agentMix: 'native_and_hosted'
  }
];

export const WorkflowStudioHome: React.FC = () => {
  const navigate = useNavigate();

  const handleWorkflowClick = (workflowId: string) => {
    navigate(`/workflows/${workflowId}?tab=design`);
  };

  const handleExecuteWorkflow = (workflowId: string) => {
    navigate(`/workflows/${workflowId}?tab=execute`);
  };

  const getOrchestrationInfo = (type: string) => {
    switch (type) {
      case 'native':
        return {
          label: 'Native Agent Orchestration',
          color: '#3b82f6',
          icon: <Zap size={16} />,
          description: 'ElevateNow certified agents',
          valueProps: ['Deep insurance domain expertise', 'Optimized performance', 'Unified intelligence']
        };
      case 'hosted':
        return {
          label: 'Hosted Agent Integration',
          color: '#9ca3af',
          icon: <GitBranch size={16} />,
          description: 'AWS Agent Code integration',
          valueProps: ['Leverage existing AI investments', 'Multi-vendor coordination', 'Flexible integration']
        };
      case 'hybrid':
        return {
          label: 'Hybrid Intelligence',
          color: '#3b82f6',
          icon: <Shield size={16} />,
          description: 'Native + hosted coordination',
          valueProps: ['Best of both worlds', 'Unified governance', 'Future-proof architecture']
        };
      default:
        return {
          label: 'Standard Workflow',
          color: 'var(--text-secondary)',
          icon: <Play size={16} />,
          description: 'Basic orchestration',
          valueProps: ['Standard processing', 'Basic coordination', 'Simple workflow']
        };
    }
  };

  const getMetricsForWorkflow = (workflowId: string) => {
    return orchestrationMetrics[workflowId as keyof typeof orchestrationMetrics] || {};
  };

  return (
    <div style={{ background: 'var(--divider-light)', minHeight: 'calc(100vh - 48px)' }}>
      {/* Header Actions */}
      <div style={{ 
        background: 'var(--card-bg)', 
        borderBottom: '1px solid var(--border-color)',
        padding: '20px 24px'
      }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '6px', color: 'var(--text-primary)' }}>
                Workflow Studio
              </h1>
              <p style={{ fontSize: '16px', color: 'var(--text-secondary)', margin: 0 }}>
                Design, execute, and monitor insurance intelligence workflows across BUILD • HOST • GOVERN patterns
              </p>
            </div>
            
            <button className="sn-button-primary" style={{
              padding: '12px 20px',
              fontSize: '14px',
              fontWeight: '600',
              borderRadius: '8px'
            }}>
              <Plus size={16} />
              New Workflow
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '32px 24px', maxWidth: '1400px', margin: '0 auto' }}>
        {/* Orchestration Overview */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
              Orchestration Patterns
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              Three distinct orchestration patterns demonstrating BUILD, HOST, and GOVERN capabilities
            </p>
          </div>

          {/* Pattern Overview Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '32px' }}>
            {['native', 'hosted', 'hybrid'].map((pattern) => {
              const info = getOrchestrationInfo(pattern);
              return (
                <div key={pattern} className="metric-card" style={{ padding: '20px', textAlign: 'left' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ color: info.color }}>{info.icon}</div>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                      {info.label}
                    </h4>
                  </div>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                    {info.description}
                  </p>
                  <div style={{ fontSize: '10px' }}>
                    {info.valueProps.map((prop, idx) => (
                      <div key={idx} style={{ 
                        color: 'var(--text-muted)', 
                        marginBottom: '2px',
                        paddingLeft: '8px',
                        position: 'relative'
                      }}>
                        <span style={{ 
                          position: 'absolute', 
                          left: '0', 
                          color: info.color 
                        }}>•</span>
                        {prop}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Core Workflows Section */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{ marginBottom: '24px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: 'var(--text-primary)' }}>
              Production Workflows
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
              Enterprise-ready workflows demonstrating different orchestration approaches
            </p>
          </div>

          {/* Workflow Tiles */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))', 
            gap: '24px'
          }}>
            {workflowTiles.map((workflow) => {
              const orchestration = getOrchestrationInfo(workflow.orchestrationType);
              const metrics = getMetricsForWorkflow(workflow.id);
              
              return (
                <div
                  key={workflow.id}
                  onClick={() => handleWorkflowClick(workflow.id)}
                  className={`workflow-card ${workflow.orchestrationType}`}
                  style={{
                    padding: '24px',
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                >
                  {/* Enhanced Status Indicator with new colors */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: workflow.orchestrationType === 'native' 
                      ? '#3b82f6' 
                      : workflow.orchestrationType === 'hosted' 
                        ? '#9ca3af' 
                        : 'linear-gradient(90deg, #3b82f6 0%, #9ca3af 100%)',
                    boxShadow: workflow.orchestrationType === 'native' 
                      ? '0 2px 4px rgba(59, 130, 246, 0.3)' 
                      : workflow.orchestrationType === 'hosted' 
                        ? '0 2px 4px rgba(156, 163, 175, 0.3)' 
                        : '0 2px 4px rgba(59, 130, 246, 0.2)'
                  }} />
                  {/* Status and Type Indicators */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '16px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        fontSize: '18px', 
                        fontWeight: '600', 
                        marginBottom: '6px',
                        color: 'var(--text-primary)'
                      }}>
                        {workflow.name}
                      </h3>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        marginBottom: '8px' 
                      }}>
                        <div style={{ color: orchestration.color }}>{orchestration.icon}</div>
                        <span style={{ 
                          fontSize: '12px', 
                          color: orchestration.color, 
                          fontWeight: '500' 
                        }}>
                          {orchestration.label}
                        </span>
                      </div>
                      <p style={{ 
                        fontSize: '14px', 
                        color: 'var(--text-secondary)', 
                        lineHeight: '1.4'
                      }}>
                        {workflow.description}
                      </p>
                    </div>
                    <div 
                      className={`provider-badge ${workflow.orchestrationType}`}
                      style={{ 
                        marginLeft: '12px',
                        padding: '3px 10px',
                        borderRadius: '12px',
                        fontSize: '10px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        border: '1px solid',
                        background: workflow.orchestrationType === 'native' 
                          ? '#3b82f6' 
                          : workflow.orchestrationType === 'hosted' 
                            ? '#9ca3af' 
                            : 'linear-gradient(135deg, #3b82f6 0%, #9ca3af 100%)',
                        color: 'white',
                        borderColor: workflow.orchestrationType === 'native' 
                          ? '#3b82f6' 
                          : workflow.orchestrationType === 'hosted' 
                            ? '#9ca3af' 
                            : '#3b82f6',
                        boxShadow: workflow.orchestrationType === 'native' 
                          ? '0 2px 4px rgba(59, 130, 246, 0.2)' 
                          : workflow.orchestrationType === 'hosted' 
                            ? '0 2px 4px rgba(156, 163, 175, 0.2)' 
                            : '0 2px 4px rgba(59, 130, 246, 0.15)'
                      }}
                    >
                      {workflow.orchestrationType.toUpperCase()}
                    </div>
                  </div>
                  
                  {/* Key Metrics */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(3, 1fr)', 
                    gap: '16px', 
                    marginBottom: '20px',
                    padding: '16px',
                    background: 'var(--divider-light)',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {workflow.executions.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        Executions
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--status-active)' }}>
                        {workflow.successRate}%
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        Success Rate
                      </div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: orchestration.color }}>
                        {metrics && 'decisionConsistency' in metrics ? `${metrics.decisionConsistency}%` : 'N/A'}
                      </div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase' }}>
                        Consistency
                      </div>
                    </div>
                  </div>

                  {/* Orchestration Details */}
                  {metrics && Object.keys(metrics).length > 0 && (
                    <div style={{ marginBottom: '20px' }}>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginBottom: '8px', fontWeight: '500' }}>
                        Orchestration Intelligence
                      </div>
                      <div style={{ fontSize: '11px', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                        {workflow.orchestrationType === 'hosted' && metrics && 'multiVendorCoordination' in metrics && (
                          <div>
                            <span style={{ color: 'var(--text-muted)' }}>Multi-vendor:</span>
                            <span style={{ fontWeight: '600', marginLeft: '4px', color: orchestration.color }}>
                              {metrics.multiVendorCoordination}%
                            </span>
                          </div>
                        )}
                        {workflow.orchestrationType === 'hybrid' && metrics && 'governanceConsistency' in metrics && (
                          <div>
                            <span style={{ color: 'var(--text-muted)' }}>Governance:</span>
                            <span style={{ fontWeight: '600', marginLeft: '4px', color: orchestration.color }}>
                              {metrics.governanceConsistency}%
                            </span>
                          </div>
                        )}
                        {workflow.orchestrationType === 'native' && metrics && 'agentConsensusRate' in metrics && (
                          <div>
                            <span style={{ color: 'var(--text-muted)' }}>Consensus:</span>
                            <span style={{ fontWeight: '600', marginLeft: '4px', color: orchestration.color }}>
                              {metrics.agentConsensusRate}%
                            </span>
                          </div>
                        )}
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Last run:</span>
                          <span style={{ fontWeight: '600', marginLeft: '4px' }}>{workflow.lastRun}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWorkflowClick(workflow.id);
                      }}
                      className="sn-button"
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '6px',
                      }}
                    >
                      View Details
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExecuteWorkflow(workflow.id);
                      }}
                      className="sn-button-primary"
                      style={{
                        flex: 1,
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '500',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                      }}
                    >
                      <Play size={16} />
                      Execute
                    </button>
                  </div>

                  {/* Subtle click hint */}
                  <div style={{ 
                    marginTop: '16px',
                    fontSize: '11px', 
                    color: orchestration.color,
                    textAlign: 'center',
                    opacity: 0.8,
                    fontWeight: '500'
                  }}>
                    Click card for workflow design view
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Platform Benefits Summary */}
        <div className="sn-panel">
          <div className="sn-panel-header">Platform Orchestration Benefits</div>
          <div className="sn-panel-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
              <div>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  color: 'var(--provider-native)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Zap size={16} />
                  BUILD Value
                </h4>
                <ul style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '16px' }}>
                  <li style={{ marginBottom: '4px' }}>Deep insurance domain expertise</li>
                  <li style={{ marginBottom: '4px' }}>Optimized performance and reliability</li>
                  <li style={{ marginBottom: '4px' }}>Unified intelligence across workflows</li>
                </ul>
              </div>
              
              <div>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  color: 'var(--provider-hosted)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <GitBranch size={16} />
                  HOST Value
                </h4>
                <ul style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '16px' }}>
                  <li style={{ marginBottom: '4px' }}>Leverage existing AI investments</li>
                  <li style={{ marginBottom: '4px' }}>Multi-vendor agent coordination</li>
                  <li style={{ marginBottom: '4px' }}>Flexible integration patterns</li>
                </ul>
              </div>
              
              <div>
                <h4 style={{ 
                  fontSize: '14px', 
                  fontWeight: '600', 
                  marginBottom: '8px',
                  color: 'var(--provider-hybrid)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Shield size={16} />
                  GOVERN Value
                </h4>
                <ul style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0, paddingLeft: '16px' }}>
                  <li style={{ marginBottom: '4px' }}>360-degree compliance monitoring</li>
                  <li style={{ marginBottom: '4px' }}>Unified governance framework</li>
                  <li style={{ marginBottom: '4px' }}>Audit-ready decision trails</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};